import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from '../../forum/app';
import Component from '../../common/Component';
import Link from '../../common/components/Link';
import listItems from '../../common/helpers/listItems';
import highlight from '../../common/helpers/highlight';
import humanTime from '../../common/utils/humanTime';
import ItemList from '../../common/utils/ItemList';
import abbreviateNumber from '../../common/utils/abbreviateNumber';
import Dropdown from '../../common/components/Dropdown';
import TerminalPost from './TerminalPost';
import SubtreeRetainer from '../../common/utils/SubtreeRetainer';
import DiscussionControls from '../utils/DiscussionControls';
import slidable from '../utils/slidable';
import classList from '../../common/utils/classList';
import DiscussionPage from './DiscussionPage';
import escapeRegExp from '../../common/utils/escapeRegExp';
import Tooltip from '../../common/components/Tooltip';
import Icon from '../../common/components/Icon';
import Avatar from '../../common/components/Avatar';
/**
 * The `DiscussionListItem` component shows a single discussion in the
 * discussion list.
 */
export default class DiscussionListItem extends Component {
  constructor() {
    super(...arguments);
    /**
     * Ensures that the discussion will not be redrawn
     * unless new data comes in.
     */
    _defineProperty(this, "subtree", void 0);
    _defineProperty(this, "highlightRegExp", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.subtree = new SubtreeRetainer(() => this.attrs.discussion.freshness, () => {
      const time = app.session.user && app.session.user.markedAllAsReadAt();
      return time && time.getTime();
    }, () => this.active());
  }
  elementAttrs() {
    return {
      className: classList('DiscussionListItem', this.attrs.className, {
        active: this.active(),
        'DiscussionListItem--hidden': this.attrs.discussion.isHidden(),
        Slidable: this.isSlidableEnabled()
      })
    };
  }
  view() {
    const attrs = this.elementAttrs();
    return m("div", attrs, this.viewItems().toArray());
  }
  viewItems() {
    const items = new ItemList();
    items.add('slidableUnderneath', this.slidableUnderneathView(), 90);
    items.add('content', this.contentView(), 80);
    const controls = DiscussionControls.controls(this.attrs.discussion, this).toArray();
    if (controls.length) {
      items.add('controls', this.controlsView(controls), 70);
    }
    return items;
  }
  controlsView(controls) {
    return m(Dropdown, {
      icon: "fas fa-ellipsis-v",
      className: "DiscussionListItem-controls",
      buttonClassName: "Button Button--icon Button--flat Slidable-underneath Slidable-underneath--right",
      accessibleToggleLabel: app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')
    }, controls);
  }
  slidableUnderneathView() {
    const discussion = this.attrs.discussion;
    const isUnread = discussion.isUnread();
    return m("span", {
      className: classList('Slidable-underneath Slidable-underneath--left Slidable-underneath--elastic', {
        disabled: !isUnread
      }),
      onclick: this.markAsRead.bind(this)
    }, m(Icon, {
      name: 'fas fa-check'
    }));
  }
  contentView() {
    const discussion = this.attrs.discussion;
    const isUnread = discussion.isUnread();
    const isRead = discussion.isRead();
    return m("div", {
      className: classList('DiscussionListItem-content', 'Slidable-content', {
        unread: isUnread,
        read: isRead
      })
    }, this.contentItems().toArray());
  }
  contentItems() {
    const items = new ItemList();
    items.add('author', this.authorView(), 100);
    items.add('main', this.mainView(), 80);
    items.add('stats', this.statsView(), 70);
    return items;
  }
  authorView() {
    return m("div", {
      className: "DiscussionListItem-author"
    }, this.authorItems().toArray());
  }
  authorItems() {
    const items = new ItemList();
    const discussion = this.attrs.discussion;
    const user = this.attrs.author || discussion.user();
    items.add('avatar', m(Tooltip, {
      text: app.translator.trans('core.forum.discussion_list.started_text', {
        user,
        ago: humanTime(discussion.createdAt())
      }),
      position: "right"
    }, user ? m(Link, {
      className: "DiscussionListItem-author-avatar",
      href: app.route.user(user)
    }, m(Avatar, {
      user: user,
      title: ""
    })) : m("span", {
      className: "DiscussionListItem-author-avatar"
    }, m(Avatar, {
      user: null,
      title: ""
    }))), 100);
    items.add('badges', this.badgesView(), 90);
    return items;
  }
  badgesView() {
    const discussion = this.attrs.discussion;
    return m("ul", {
      className: "DiscussionListItem-badges badges badges--packed"
    }, listItems(discussion.badges().toArray()));
  }
  mainView() {
    const discussion = this.attrs.discussion;
    const jumpTo = this.getJumpTo();
    return m(Link, {
      href: app.route.discussion(discussion, jumpTo),
      className: "DiscussionListItem-main"
    }, m("h2", {
      className: "DiscussionListItem-title"
    }, highlight(discussion.title(), this.highlightRegExp)), m("ul", {
      className: "DiscussionListItem-info"
    }, listItems(this.infoItems().toArray())));
  }
  getJumpTo() {
    if (this.attrs.jumpTo) {
      return this.attrs.jumpTo;
    }
    const discussion = this.attrs.discussion;
    let jumpTo = 0;
    if (this.attrs.params.q) {
      const post = discussion.mostRelevantPost();
      if (post) {
        jumpTo = post.number();
      }
      const phrase = escapeRegExp(this.attrs.params.q);
      this.highlightRegExp = new RegExp(phrase + '|' + phrase.trim().replace(/\s+/g, '|'), 'gi');
    } else {
      jumpTo = Math.min(discussion.lastPostNumber() ?? 0, (discussion.lastReadPostNumber() || 0) + 1);
    }
    return jumpTo;
  }
  isSlidableEnabled() {
    if (this.attrs.slidable === false) {
      return false;
    }
    return 'ontouchstart' in window;
  }
  oncreate(vnode) {
    super.oncreate(vnode);

    // If we're on a touch device, set up the discussion row to be slidable.
    // This allows the user to drag the row to either side of the screen to
    // reveal controls.
    if (this.isSlidableEnabled()) {
      const slidableInstance = slidable(this.element);
      this.$('.DiscussionListItem-controls').on('hidden.bs.dropdown', () => slidableInstance.reset());
    }
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    return this.subtree.needsRebuild();
  }

  /**
   * Determine whether or not the discussion is currently being viewed.
   */
  active() {
    return app.current.matches(DiscussionPage, {
      discussion: this.attrs.discussion
    });
  }

  /**
   * Determine whether or not information about who started the discussion
   * should be displayed instead of information about the most recent reply to
   * the discussion.
   */
  showFirstPost() {
    return ['newest', 'oldest'].includes(this.attrs.params.sort ?? '');
  }

  /**
   * Determine whether or not the number of replies should be shown instead of
   * the number of unread posts.
   *
   * @return {boolean}
   */
  showRepliesCount() {
    return this.attrs.params.sort === 'replies';
  }

  /**
   * Mark the discussion as read.
   */
  markAsRead() {
    const discussion = this.attrs.discussion;
    if (discussion.isUnread()) {
      discussion.save({
        lastReadPostNumber: discussion.lastPostNumber()
      });
      m.redraw();
    }
  }

  /**
   * Build an item list of info for a discussion listing. By default this is
   * just the first/last post indicator.
   */
  infoItems() {
    const items = new ItemList();
    if (this.attrs.params.q) {
      const post = this.attrs.post || this.attrs.discussion.mostRelevantPost() || this.attrs.discussion.firstPost();
      if (post && post.contentType() === 'comment') {
        const excerpt = highlight(post.contentPlain() ?? '', this.highlightRegExp, 175);
        items.add('excerpt', excerpt, -100);
      }
    } else {
      items.add('terminalPost', m(TerminalPost, {
        discussion: this.attrs.discussion,
        lastPost: !this.showFirstPost()
      }));
    }
    return items;
  }
  statsView() {
    return m("div", {
      className: "DiscussionListItem-stats"
    }, this.statsItems().toArray());
  }
  statsItems() {
    const items = new ItemList();
    items.add('replyCount', this.replyCountItem(), 70);
    return items;
  }
  replyCountItem() {
    const discussion = this.attrs.discussion;
    const showUnread = !this.showRepliesCount() && discussion.isUnread();
    const a11yKey = showUnread ? 'core.forum.discussion_list.unread_replies_a11y_label' : 'core.forum.discussion_list.total_replies_a11y_label';
    return m(DiscussionListItemStatsItem, {
      className: "DiscussionListItem-count",
      icon: showUnread ? [m(Icon, {
        name: 'fas fa-check _checkmark'
      }), m(Icon, {
        name: 'fas fa-comment _comment'
      })] : m(Icon, {
        name: 'far fa-comment'
      }),
      label: showUnread ? abbreviateNumber(discussion.unreadCount()) : abbreviateNumber(discussion.replyCount()),
      ariaLabel: app.translator.trans(a11yKey, {
        count: discussion.unreadCount()
      }),
      onclick: showUnread ? this.markAsRead.bind(this) : undefined
    });
  }
}
export class DiscussionListItemStatsItem extends Component {
  view(vnode) {
    const {
      icon,
      label,
      a11yLabel,
      className,
      ...attrs
    } = vnode.attrs;
    const Tag = attrs.onclick ? 'button' : 'span';
    return m(Tag, Object.assign({
      className: classList('DiscussionListItem-stats-item', className, {
        'Button--ua-reset': Tag === 'button'
      })
    }, attrs), m("span", {
      className: "DiscussionListItem-stats-item-icon"
    }, icon), m("span", {
      className: "DiscussionListItem-stats-item-label"
    }, m("span", {
      "aria-hidden": "true"
    }, label), m("span", {
      className: "visually-hidden"
    }, a11yLabel ?? label)));
  }
}
flarum.reg.add('core', 'forum/components/DiscussionListItem', DiscussionListItem);